/**
 * Central tuning knobs. Everything a designer wants to tweak lives here so the
 * simulation code stays declarative. Values are in SIM units:
 *   distance = world pixels, time = seconds, speed = px/s.
 */

/** Performance tiers — the render layer scales entity/particle budgets by device. */
export const PERF = {
  /** Hard caps so a mid-range phone never melts. */
  maxDrones: 64,
  maxSwarmShips: 200,
  maxParticles: 1200,
  /** Cap devicePixelRatio; retina phones otherwise render 3x the pixels. */
  maxPixelRatio: 2,
} as const;

/** Boids/flocking weights & radii (see game/combat/boids.ts). */
export const FLOCK = {
  perceptionRadius: 90,
  separationRadius: 34,
  maxSpeed: 220,
  maxForce: 520,
  weights: {
    separation: 1.8,
    alignment: 1.0,
    cohesion: 0.9,
    seek: 1.4, // chase assigned enemy
    flee: 1.6, // evade incoming threat
  },
} as const;

/** Mining economy (Level 1). */
export const MINING = {
  droneCapacity: 25, // minerals a drone hauls per trip
  droneSpeed: 140, // px/s
  extractRatePerSec: 12, // minerals/s while docked on an asteroid
  depositRatePerSec: 60, // minerals/s while unloading at the mothership
} as const;

/** Durability & repair economy (see game/combat/durability.ts). */
export const REPAIR = {
  /** Active (shipyard) repair: cheap, restores to 100%. */
  activeCostPerPoint: { minerals: 2 },
  /** Idle (on-board technician) repair: pricier, caps at 80%. */
  idleCapPercent: 80,
  idleCostPerPoint: { minerals: 6 },
  /** Durability points restored per second per technician tier. */
  idleRepairRatePerTier: 0.5,
} as const;

/**
 * Player-piloted mothership. At Level 1 the player has NO drones and must fly
 * the mothership onto asteroids to mine directly, earning enough minerals to
 * build the first drones.
 */
export const MOTHERSHIP = {
  speed: 90, // px/s base — deliberately slow (space is big); Engine upgrades raise it
  turnRate: 2.4, // rad/s heading change — the ship banks into curves, never snaps
  arriveRadius: 6, // stop distance from the target
  miningRange: 74, // hull-to-asteroid distance at which direct mining kicks in
  beamRate: 4, // minerals/s PER mining beam (Laser upgrades raise this)
  baseBeams: 2, // hull starts with 2 beams → 8/s total; beam upgrades add more
  spriteWorldHeight: 52, // rendered sprite size (halved — ships now sit below moon-scale)
} as const;

/**
 * Hull defense. Flying into a rock damages the SHIELD first; once the shield is
 * gone the HULL (HP) takes the hit. Shield regenerates after a short lull but
 * BURNS MINERALS to do so; HP does not regenerate (repair comes later).
 */
export const SHIP_DEFENSE = {
  collisionRadius: 26, // hull core radius for rock collisions (world units)
  collisionDamage: 16, // damage per bump (discrete — the ship bounces off)
  bounceTime: 0.35, // seconds the ship is flung away after a hit (also i-frames)
  bounceSpeed: 160, // px/s knock-back speed off the rock
  slowWhileMining: 0.5, // ship speed multiplier while the hull is mining
  maxShield: 40, // small by default — shield generators (mods) raise it
  shieldRegenPerSec: 10, // shield points restored per second (× equipped mod mul)
  shieldRegenCostPerPoint: 0.6, // minerals spent per shield point restored
  regenDelay: 2.5, // seconds after a hit before the shield starts recharging
  shieldDepleteDelay: 5, // longer cooldown before regen once the shield fully breaks
  // Shield-bubble radius multiplier per hull tier — also the incoming-fire hitbox
  // while the shield is up (must match the PixiLayer bubble VFX).
  shieldBubbleMul: [0.7, 1.5, 2.2, 3.5, 4.2, 4.7], // Mk I..VI (VI = the bigger Battleship)
  mk4CollisionMul: 2.7, // Mk IV hitbox ~ the big hull, so rocks are shoved at the edge (no lag)
  mk4CollisionDamage: 4, // Mk IV plows through rocks — only a little shield lost
  // Mechanical-planet services:
  repairCostPerHp: 3, // minerals per hull point restored
  shieldUpgradeStep: 25, // +max shield per shield upgrade
  shieldUpgradeBaseCost: 700, // minerals for the 1st shield upgrade (scales with level)
} as const;

/**
 * MK V — the endgame tier, refit from the Carrier (Mk IV) into ONE of two paths:
 * Battleship (firepower) or Swarm-lord (fleet swarm). The hull is 20% bigger than
 * the Carrier, but turrets / cannons / fighters / drones keep their size. Per-path
 * arsenal caps + fighter-drone / crew tuning here are consumed as later phases wire
 * the actual weapons, drones, and crew in.
 */
export const MKV = {
  refitMinerals: 1_000_000,
  refitUnits: 5_000,
  refitCores: 10,
  hullScaleMul: 1.2, // 20% bigger than the Carrier hull
  btsShieldBonus: 100, // Battleship path trait: +100 max shield
  fighterDroneSizeMul: 0.6, // fighter drones render 40% smaller than a normal fighter
  // Per-path arsenal caps (applied when the arsenal-cap phase lands).
  bts: { turrets: 14, cannons: 4, bigCannons: 2, fighters: 8 },
  swl: { turrets: 14, cannons: 2, fighters: 12, fighterDrones: 12 },
} as const;

/**
 * MK VI Battleship — the next hull above the Mk V Battleship: bigger (renders ~368
 * world units), 18 turrets, 8 spinal cannons, 4 Big Cannons (2 per side, off the
 * spine), and a 6-silo homing-missile salvo. Refit is gated behind mkv_bts.
 */
export const MKVI = {
  refitMinerals: 5_000_000,
  refitUnits: 25_000,
  refitCores: 30,
  hullRenderMul: 7.077, // renders ~368 world units tall (MOTHERSHIP.spriteWorldHeight 52 × 7.077)
  btsShieldBonus: 250, // Mk VI Battleship trait: +250 max shield
  swlShieldBonus: 150, // Mk VI Swarm-lord trait: +150 max shield (a tougher carrier hull)
  swlSizeMul: 1.1, // Mk VI Swarm-lord renders 10% larger than the Battleship (hull + mounts + runways)
  // --- Mk VII Swarm-lord: a tier-5 Swarm-lord VARIANT (bigger + cosmic art, new mount layout) ---
  swl7SizeMul: 1.32, // Mk VII Swarm-lord renders ~485 world units (Mk VI SWL 405 × 1.2)
  swl7ShieldBonus: 250, // Mk VII Swarm-lord: +250 max shield (bigger cosmic hull)
  swl7: { turrets: 12, cannons: 8, fighters: 16, fighterDrones: 28 }, // same arsenal caps as Mk VI SWL
  // Rift Swarm skill: 2 rifts (one per side), each disgorging `perRift` allied NPC craft (random
  // fighter/drone mix) that fight for `lifetime`s then vanish. Manual button on a `cooldown`.
  swl7Rift: {
    perRift: 4, // purple arrows released per WAVE, per rift
    maxPerRift: 10, // ...up to this many total per rift (waves of 4 → 4+4+2 = 10; 20 across both rifts)
    batchInterval: 1, // seconds between waves (20 ÷ 4 = 5 waves stream out over the open window)
    lifetime: 30, // seconds an arrow flies before it fades
    cooldown: 90,
    side: 240, // rift offset to each side of the ship
    portalLife: 6, // rift hangs still ~5s, then fades over the last 1s (no spin)
    retarget: 5, // re-pick a target every 5s if the current one survives
    arrowSpeedMul: 1.7, // 70% faster than an escort fighter
    turnRate: 5.5, // rad/s homing
    damage: 34, // per pierce hit
    hitRadius: 28,
    hitInterval: 0.4, // min seconds between an arrow's damage ticks
    trailLen: 26, // long trail
  },
  // 6 physical missile silos (salvo SIZE is upgrade-driven — see the Missile Silo nodes
  // in upgradeTree.ts; the salvo streams 2 at a time cycling through these launch points).
  // MTS_VI_BTS 1024×1536 → ÷3 to the 512 reference, ordered as front→back PAIRS
  // (one per side each batch): R1,L1 / R2,L2 / R3,L3. oy: +y = nose.
  missileSilos: [
    { ox: 81, oy: -71 }, { ox: -81, oy: -71 },
    { ox: 66, oy: -14 }, { ox: -66, oy: -14 },
    { ox: 59, oy: 34 }, { ox: -59, oy: 34 },
  ],
  bts: { turrets: 18, cannons: 8, bigCannons: 4 },
  // Swarm-lord path: light defensive turrets + a bigger drone swarm, no Big Cannons.
  swl: { turrets: 12, cannons: 8, fighters: 16, fighterDrones: 28 },
  // Mk VI Swarm-lord mount map (from MK_VI_SWL_Mapping.txt on MTS_VI_SWL 1016×1497, ÷2.924 to
  // the 512 reference; oy: +y = nose/texture-bottom). 8 wing hangars (drone launch) + 18 missile
  // hardpoints. Turrets live in turrets.ts (TURRET_MOUNTS_MKVI_SWL), cannons in CANNON.mountsSwlVI.
  swlHangars: [
    { ox: 91.0, oy: -91.7 }, { ox: -91.0, oy: -91.7 },
    { ox: 120.0, oy: -80.0 }, { ox: -120.0, oy: -80.0 },
    { ox: 78.0, oy: -47.2 }, { ox: -78.0, oy: -47.2 },
    { ox: 102.3, oy: -37.3 }, { ox: -102.3, oy: -37.3 },
  ],
  swlMissiles: [
    { ox: 162.8, oy: -74.6 }, { ox: -162.8, oy: -74.6 },
    { ox: 137.5, oy: -54.4 }, { ox: -137.5, oy: -54.4 },
    { ox: 120.7, oy: -27.7 }, { ox: -120.7, oy: -27.7 },
    { ox: 106.0, oy: -6.8 }, { ox: -106.0, oy: -6.8 },
    { ox: 84.5, oy: 18.5 }, { ox: -84.5, oy: 18.5 },
    { ox: 150.5, oy: -63.3 }, { ox: -150.5, oy: -63.3 },
    { ox: 127.9, oy: -42.1 }, { ox: -127.9, oy: -42.1 },
    { ox: 113.5, oy: -17.1 }, { ox: -113.5, oy: -17.1 },
    { ox: 95.8, oy: 4.4 }, { ox: -95.8, oy: 4.4 },
  ],
  // Mk VII Swarm-lord mount map (MKVIISWL Mapping.txt on MTS_VII_SWL 1024×1536, ÷3 to the 512
  // reference; oy: +y = nose). 8 wing hangars (drone launch) + 12 missile hardpoints. Turrets in
  // turrets.ts (TURRET_MOUNTS_MKVII_SWL), cannons in CANNON.mountsSwl7.
  swl7Hangars: [
    { ox: 94.3, oy: -87.3 }, { ox: -94.3, oy: -87.3 },
    { ox: 119.3, oy: -77.0 }, { ox: -119.3, oy: -77.0 },
    { ox: 71.7, oy: -39.0 }, { ox: -71.7, oy: -39.0 },
    { ox: 96.0, oy: -30.0 }, { ox: -96.0, oy: -30.0 },
  ],
  swl7Missiles: [
    { ox: 62.0, oy: 50.7 }, { ox: -62.0, oy: 50.7 },
    { ox: 74.0, oy: 28.7 }, { ox: -74.0, oy: 28.7 },
    { ox: 88.7, oy: 8.7 }, { ox: -88.7, oy: 8.7 },
    { ox: 105.7, oy: -8.3 }, { ox: -105.7, oy: -8.3 },
    { ox: 114.3, oy: -20.3 }, { ox: -114.3, oy: -20.3 },
    { ox: 125.0, oy: -41.3 }, { ox: -125.0, oy: -41.3 },
  ],
} as const;

/**
 * Carrier turrets — side-mounted twin cannons. Mk II unlocks 4 mounts, Mk III
 * unlocks 8. Each turret is limited to its own side's 180° arc (it can sweep
 * from dead-ahead to dead-astern on its side but not across the hull), each
 * locks its own target, and all can converge on a target directly in front.
 */
export const TURRET = {
  slots: 8, // Mk II uses the first 4; Mk III unlocks all 8
  slotsMkII: 4,
  slotsMkV: 14, // Mk V hulls mount 14 turrets
  slotsMkVI: 18, // Mk VI Battleship mounts 18 turrets
  range: 360, // target acquisition + firing range
  turnRate: 4.5, // rad/s aim speed
  fireInterval: 1, // seconds between shots (1 shot/sec)
  boltsPerShot: 2, // 2 barrels → 2 bolts side by side
  boltSpeed: 460, // px/s
  boltDamage: 12, // per bolt (hits shield then hull)
  boltLife: 1.4, // seconds before a bolt fizzles
  barrelSpread: 5, // half-distance between the twin barrels
  muzzleLen: 10, // bolt spawn distance ahead of the mount
  // Units to unlock each successive slot (index 0 = first slot). 14 entries for Mk V.
  slotUnlockCost: [
    40, 90, 180, 320, 480, 660, 860, 1080, 1320, 1580, 1860, 2160, 2480, 2840,
    3220, 3620, 4040, 4480,
  ],
  buyCost: 60, // Units to buy one Twin Cannon at the arsenal
} as const;

/**
 * Spinal CANNONS — 3 manually-aimed heavy guns down the Mk IV centre line. The
 * player picks an aim point; the cannons slowly rotate to it, then fire twin big
 * bolts that devastate moons (obliterating small ones in a shot).
 */
export const CANNON = {
  slots: 3,
  // Central slots on MTS_4 (257×512 texture offsets from centre).
  mounts: [
    { ox: 0, oy: -63 },
    { ox: 0, oy: 44 },
    { ox: 0, oy: 154 },
  ],
  // Mk V Battleship (MTS_V_BTS 237×512): 4 cannons — a pair "far back" on the
  // superstructure shoulders + a pair on the inner-flank sockets. The 2 Big Cannons
  // sit on the centre spine (see bigMountsBts, a separate slot system).
  // oy: +y = toward the nose (texture bottom); superstructure sits at −oy.
  mountsBts: [
    { ox: -60, oy: -193 }, // far-back left (superstructure shoulder)
    { ox: 60, oy: -193 }, // far-back right
    { ox: -35, oy: -102 }, // inner-flank left
    { ox: 35, oy: -102 }, // inner-flank right
  ],
  // Mk V Swarm-lord (MTS_V_SWL 402×512): 2 cannons stacked on the centre spine.
  mountsSwl: [
    { ox: 0, oy: -111 }, // upper spine socket (toward the superstructure)
    { ox: 0, oy: -64 }, // lower spine socket
  ],
  // Mk V Battleship Big Cannons — 2 slots on the centre spine (the two largest
  // sockets). Crafted, not bought; fire the twin map-length beams (Big Cannon system).
  bigMountsBts: [
    { ox: 0, oy: -8 }, // upper centre-spine big socket
    { ox: 0, oy: 55 }, // lower centre-spine big socket
  ],
  // Mk VI Battleship (MTS_VI_BTS 1024×1536 → ÷3 to the 512 reference): 8 spinal cannons.
  mountsBtsVI: [
    { ox: -22, oy: -143 }, { ox: 22, oy: -143 },
    { ox: -14, oy: -96 }, { ox: 14, oy: -96 },
    { ox: -28, oy: -60 }, { ox: 28, oy: -60 },
    { ox: -16, oy: -24 }, { ox: 16, oy: -24 },
  ],
  // Mk VI Battleship — 4 Big Cannons, 2 per side (off the spine, on the flanks).
  bigMountsBtsVI: [
    { ox: -52, oy: -101 }, { ox: 52, oy: -101 },
    { ox: -26, oy: 31 }, { ox: 26, oy: 31 },
  ],
  // Mk VI Swarm-lord (MTS_VI_SWL 1016×1497 → ÷2.924 to the 512 reference): 8 cannons, from
  // MK_VI_SWL_Mapping.txt. oy: +y = nose/texture-bottom.
  mountsSwlVI: [
    { ox: -73.9, oy: -71.1 }, { ox: 73.9, oy: -71.1 },
    { ox: -16.8, oy: -153.6 }, { ox: 16.8, oy: -153.6 },
    { ox: -29.4, oy: -229.5 }, { ox: 29.4, oy: -229.5 },
    { ox: 0.3, oy: -128.6 }, { ox: 0.3, oy: -91.7 },
  ],
  // Mk VII Swarm-lord — 8 cannons (MKVIISWL Mapping.txt ÷3 to the 512 reference).
  mountsSwl7: [
    { ox: -28.3, oy: 20.0 }, { ox: 28.3, oy: 20.0 },
    { ox: -15.7, oy: -138.3 }, { ox: 15.7, oy: -138.3 },
    { ox: -30.0, oy: -205.3 }, { ox: 30.0, oy: -205.3 },
    { ox: -75.3, oy: -114.0 }, { ox: 75.3, oy: -114.0 },
  ],
  turnRate: 0.85, // rad/s — deliberately slow
  fireCone: 0.14, // must be roughly on target to fire
  boltsPerShot: 2, // twin big bolts per cannon
  barrelSpread: 7, // the twin bolts fly PARALLEL, this far apart (perpendicular)
  inlineSpread: 26, // vs ENEMY ships: stagger the bolts IN-LINE (along the aim) this far
  boltSpeed: 340,
  boltLife: 2.4,
  boltDamage: 22, // vs ships (halved)
  moonDamage: 600, // vs moons/asteroids (halved) — still wrecks small moons fast
  fireInterval: 1.5,
  spriteWorldHeight: 30, // rendered cannon size (universal, like turrets)
  buyCost: 120, // Units to buy one spinal cannon
  // (Cannon slot-unlock fees continue the TURRET.slotUnlockCost curve past the 8th
  // turret port — see GameState.nextCannonSlotCost.)
} as const;

/**
 * BIG CANNONS — the Mk V Battleship's 2 centre-spine super-guns. They are CRAFTED
 * (not bought): 5 Dark Matter + 500 Units each. Once equipped they fire twin
 * beams that reach clean across the field, piercing everything in their line.
 */
export const BIG_CANNON = {
  slots: 4, // up to 4 big cannons (Mk VI Battleship; Mk V uses only 2 — capped per hull)
  craftDarkMatter: 5,
  craftUnits: 500,
  slotUnlockCost: [1200, 2000, 2800, 3600], // Units to unlock big-cannon slots 1..4
  spriteWorldHeight: 62, // rendered size (~2× a normal cannon)
  turnRate: 0.324, // rad/s — very ponderous (−10%, then another −20%)
  fireCone: 0.1, // must be lined up to open fire
  fireInterval: 5.0, // seconds between beam volleys (own targeting, long cooldown)
  // Persistent "kamehameha" beam: each barrel emits a beam that creeps out to
  // beamMaxLength, holds for beamDuration, then vanishes — dealing CONTINUOUS
  // damage to everything along its length (twin beams per gun).
  beamDuration: 1.52, // seconds the beam stays on (+10%, then +20%)
  beamGrowTime: 3.0, // seconds to creep out to full length (another 60% slower beam)
  beamMaxLength: 2400, // beam reach — clean across the field
  barrelLen: 42, // beam emerges this far ahead of the mount (the muzzle tip)
  // Only ACQUIRE/fire at enemies within the spinal cannons' reach (bolt travel),
  // so the Big Cannons engage the same area the cannons do (not the full beam length).
  targetRange: CANNON.boltSpeed * CANNON.boltLife,
  dps: 75, // damage/sec vs ships (per beam) — halved
  moonDps: 800, // ore carved/sec vs moons/asteroids (per beam) — halved
  barrelSpread: 12, // half-distance between the twin beams (parallel)
  beamWidth: 9, // beam thickness / hit radius
} as const;

/** Neutral NPC miner ships (MTS_1) that wander a planet system mining rocks. */
export const NPC = {
  count: 4, // ships per planet system
  speed: 70, // px/s cruise
  turnRate: 2.2, // rad/s steering
  miningRange: 74, // matches the hull's base mining reach
  mineRatePerSec: 7, // how fast an NPC chews a rock
  attackHold: 3, // seconds on one impact spot before wandering it
  maxHp: 80,
  maxShield: 60,
  shieldRegenPerSec: 8,
  regenDelay: 3,
  collisionRadius: 24,
  collisionDamage: 14,
  bounceTime: 0.35,
  bounceSpeed: 150,
} as const;

/**
 * The FIGHTER — a hostile raider (its own sprite). Mk II-class hull with 1–2
 * FORWARD-firing red turrets. It patrols slowly (dodging rocks) until the player
 * enters aggro range, then makes strafing runs: charge head-on firing forward,
 * peel off and loop around when close, then swing back for another pass.
 * Killable by the player's turrets; respawns at the edge.
 */
export const ENEMY = {
  baseCount: 2, // fighters per planet system at warp 0
  perWarp: 0.5, // +1 every two warps
  maxCount: 6,
  speed: 240, // px/s — fast aggressive dogfight passes (~70% quicker than before)
  patrolSpeed: 60, // px/s roaming drift when not aggro
  turnRate: 3.0, // rad/s steering
  aggroRange: 600, // player must come this close to trigger aggro
  passRange: 150, // once this close it commits to a straight fly-past
  straightTime: 2, // seconds flying straight past before curving back for another run
  minSpawnDist: 640, // never spawn nearer than this to the player's entry point
  maxHp: 120,
  maxShield: 70,
  shieldRegenPerSec: 6,
  regenDelay: 4,
  collisionRadius: 20,
  avoidRange: 70, // extra stand-off kept from asteroids while steering
  respawnDelay: 14, // seconds dead before a fresh fighter warps in
  killXp: 30, // player XP for destroying one
  killUnits: 4, // Universal Units awarded for a kill (neutral miners give none)
  // Twin red turrets — 2 forward mounts (front quarters of the hull).
  mounts: [
    { lat: -12, fwd: -10 },
    { lat: 12, fwd: -10 },
  ],
  turretRange: 520,
  fireCone: 0.34, // fire only when the nose is within this angle of the target
  // Fighter cadence — also copied by PLAYER_FIGHTER below, so this nerfs BOTH the
  // enemy raiders and the player's escort fighters. ×1.25 interval = ~20% fewer bolts.
  fireInterval: (TURRET.fireInterval / (1.3 * 1.5)) * 1.25, // seconds between shots (per turret)
  boltsPerShot: 2, // twin barrels → 2 red bolts per turret
  barrelSpread: 4, // half-distance between a turret's twin barrels
  boltSpeed: 430,
  boltDamage: 8, // per red bolt (hits player shield then hull)
  boltLife: 1.7,
  muzzleLen: 10,
  spriteWorldHeight: 26, // rendered hull height (25% smaller than the fleet, then ×0.5)
} as const;

/**
 * CRUISER — a rare capital-ship raider (its own big dark/red hull). Appears only
 * from the 4th drive jump onward, never more than 2 per planet layer. It is slow
 * (about 20% faster than the player's Mk IV Battleship) and fights by holding a
 * standoff and turning BROADSIDE to the player so its 2–4 rotatable side turrets
 * can bombard — those turrets only bear on the flanks, exactly like the player's.
 */
export const CRUISER = {
  fromWarp: 4, // only spawns once the player has used the drive 4+ times
  maxPerLayer: 2, // hard cap of 2 in any planet layer
  perWarp: 0.5, // ramps 1 → 2 with deeper jumps (capped at maxPerLayer)
  speedMul: 1.2, // cruise = 20% faster than the player's current (Mk IV) speed
  speedMin: 96, // ...clamped so it's never trivially slow
  speedMax: 190, // ...nor as fast as a fighter (240)
  patrolSpeed: 55, // slow drift when not aggro
  turnRate: 1.1, // ponderous hull steering (rad/s)
  aggroRange: 820, // engages the player within this range
  standoff: 540, // preferred broadside orbit distance
  minSpawnDist: 780, // never spawn nearer than this to the player's entry
  maxHp: 360,
  maxShield: 160,
  shieldRegenPerSec: 9,
  regenDelay: 4,
  collisionRadius: 69, // big hull (50% bigger)
  avoidRange: 90,
  turretMin: 2, // equips 2..4 side turrets
  turretMax: 4,
  turretTurn: 1.8, // turret rotation speed (rad/s)
  turretArc: 1.15, // ± sweep each side turret has around its beam (broadside only)
  fireCone: 0.13, // fires only when a turret is lined up this close
  turretRange: 720,
  fireInterval: 0.88, // seconds between shots, per turret (~30% faster than 1.15)
  boltsPerShot: 2, // twin bolts, side by side
  barrelSpread: 6, // half-distance between a turret's twin barrels (parallel)
  boltSpeed: 470,
  boltDamage: 6, // per red bolt (hits player shield then hull)
  boltLife: 2.2,
  muzzleLen: 16,
  killXp: 120,
  killUnits: 20,
  // --- call-for-backup (jump-drive reinforcements) ---
  backupFromWarp: 6, // ability unlocked from the 6th drive jump onward
  backupSquadMin: 2, // summons 2..3 fighters
  backupSquadMax: 3,
  backupRadioTime: 3, // seconds of radio-wave broadcast before they warp in
  backupWarpSpeedMul: 10, // fighters streak in at 1000% speed
  backupHoldTime: 1, // hold at the cruiser's side 1s, then aggro
  backupArriveGap: 46, // formation spacing beside the cruiser
  // Side turret mounts (world units) — sit on the sprite's flank gun clusters.
  // Order: mid-left, mid-right (used first at 2 turrets), then aft-left, aft-right.
  // Turret mounts as texture-pixel ox/oy (like the player's Mk III/V), measured on
  // enemy_cruiser.png (301×512; nose at bottom). +oy = toward the nose (texture bottom).
  mounts: [
    { ox: -53, oy: 132 }, // fore-left socket
    { ox: 53, oy: 132 }, // fore-right socket
    { ox: -48, oy: -81 }, // aft-left socket
    { ox: 48, oy: -81 }, // aft-right socket
  ],
  spriteTexHeight: 512, // enemy_cruiser.png height (drives the ox/oy → world transform)
  spriteWorldHeight: 78, // 50% bigger than before (was 52; 3× the fighter)
} as const;

/**
 * ENEMY_CARRIER — a hostile capital CARRIER (its own big dark hull, evil red glow).
 * A rarer, tougher cousin of the Cruiser that appears from the 6th drive jump onward
 * (max 1 per planet layer). It holds a long standoff and tracks the player with 8
 * free-rotating turrets (same slot count as the player's Mk III). Its signature: it
 * launches a wing of 6 fighters from its side hangar bays — and those 6 are ONE-SHOT,
 * they never respawn once destroyed. Like the Cruiser it can also call ONE jump-in
 * squad per life. Satisfies the `Damageable` shape, so player guns/lock-on hit it.
 */
export const ENEMY_CARRIER = {
  fromWarp: 10, // only spawns from the 10th drive jump onward
  maxPerLayer: 1, // a rare mini-boss — never more than 1 per planet layer
  perWarp: 0, // stays at 1 (does not ramp with deeper jumps)
  speedMul: 0.9, // cruise = slightly slower than the player's current speed
  speedMin: 70, // ...clamped so it's never dead in the water
  speedMax: 150, // ...nor as fast as a cruiser
  patrolSpeed: 45, // ponderous drift when not aggro
  turnRate: 0.85, // very heavy hull steering (rad/s)
  aggroRange: 900, // engages the player within this range
  standoff: 640, // preferred orbit distance (keeps its fighter screen ahead of it)
  minSpawnDist: 950, // never spawn nearer than this to the player's entry
  maxHp: 1100, // a big damage sponge
  maxShield: 400,
  shieldRegenPerSec: 10,
  regenDelay: 4,
  collisionRadius: 120, // huge hull (+30%)
  avoidRange: 100,
  // 8 flank-arc turrets (same slot count as the player's Mk III): each only bears on
  // its own side, so at most ~4 fire on the player at once.
  turretTurn: 1.6, // turret rotation speed (rad/s)
  fireCone: 0.12, // fires only when a turret is lined up this close
  turretArc: 1.15, // ± sweep each side turret has around its flank beam (like the cruiser)
  turretRange: 780,
  fireInterval: 1.05, // seconds between shots, per turret
  boltsPerShot: 2, // twin parallel bolts
  barrelSpread: 6, // half-distance between a turret's twin barrels
  boltSpeed: 470,
  boltDamage: 6, // per red bolt (hits player shield then hull)
  boltLife: 2.4,
  muzzleLen: 18,
  killXp: 320, // fat bounty for a capital kill
  killUnits: 60,
  // --- hangar wing: its 6 fighters (one-shot — NEVER respawn once destroyed) ---
  fighters: 6, // total launched over its life
  launchInterval: 1.4, // seconds between each 2-fighter batch (one per bay)
  launchStraight: 1, // seconds a launched fighter flies straight out before engaging
  // --- call-for-backup (jump-in squad, ONCE per life — same as the Cruiser) ---
  backupFromWarp: 6,
  backupSquadMin: 2,
  backupSquadMax: 3,
  backupRadioTime: 3,
  // Turret mounts as texture-pixel ox/oy (like the player's Mk III/V), measured on the
  // 8 gun ports of Enemy_Carrier.png (1024×1536). The hull renders FLIPPED (nose-first,
  // `heading + PI/2`) since its nose art is at the top, so the transform + arc use that.
  mounts: [
    { ox: -80, oy: -480 }, // top pair
    { ox: 80, oy: -480 },
    { ox: -105, oy: -208 }, // upper-mid pair
    { ox: 105, oy: -208 },
    { ox: -112, oy: 60 }, // lower-mid pair
    { ox: 112, oy: 60 },
    { ox: -126, oy: 200 }, // bottom pair
    { ox: 126, oy: 200 },
  ],
  hangarGates: [
    { lat: -34, fwd: 2 }, // port hangar bay
    { lat: 34, fwd: 2 }, // starboard hangar bay
  ],
  spriteTexHeight: 1536, // Enemy_Carrier.png height (drives the ox/oy → world transform)
  spriteWorldHeight: 146, // rendered hull height (+30%; nose flipped to fly nose-first)
} as const;

/**
 * EATER OF WORLDS (WORM) — the first BIG BOSS. A cosmic segmented serpent that
 * hibernates inside a planet and awakens when disturbed. It is a chain of one HEAD +
 * 24..36 BODY segments + one TAIL, each an independently-damageable node. The body
 * follows the head's path (follow-the-leader). The head fires a charged forward beam
 * that carves asteroids, moons and even other enemies; every body segment aims and
 * fires its own bolt on its own cooldown. Destroyed segments stay as burning wreckage.
 * The head is always damageable but can't be reduced below 50% until every body segment
 * is destroyed — then the head can be finished off to defeat the boss.
 */
export const WORM = {
  fromWarp: 15, // only available from the 15th drive jump onward
  spawnChance: 0.01, // 1% roll when the first moon of a planet is destroyed (MK V+)
  minSegments: 24, // body-segment count is random in [min, max]
  maxSegments: 36,
  spacing: 85, // world-units between consecutive segment centres along the path (+20%, +30%)
  trailStep: 6, // path is sampled every this-many world-units of head travel
  headWorldHeight: 205, // rendered heights (+20%, then +30%)
  bodyWorldHeight: 140,
  tailWorldHeight: 163,
  // --- movement (ponderous but relentless) ---
  speed: 155,
  turnRate: 0.97, // rad/s head steering (40% slower, then +20%)
  weaveAmp: 0.5, // lateral weave amplitude (rad) as it hunts
  weaveFreq: 1.05, // weave frequency (rad/s)
  collisionRadius: 72, // per-segment hit radius (+20%, then +30%)
  // --- HP ---
  headMaxHp: 4200,
  headFloorFrac: 0.5, // head clamped at ≥50% HP until all body segments are dead
  bodyMaxHp: 320,
  tailMaxHp: 420,
  segShield: 0,
  // --- body-segment bolts (each segment fires its own) ---
  fireInterval: 2.3, // seconds between shots per segment
  boltSpeed: 300,
  boltDamage: 7, // per bolt (player shield then hull)
  boltLife: 3.2,
  muzzleLen: 20,
  // --- head charged beam ---
  beamCooldown: 15, // seconds between blasts
  beamCharge: 2.0, // seconds of telegraph/charge before firing (head aims at player)
  beamDuration: 2.0, // seconds the beam fires
  beamLength: 1700, // world-units reach
  beamWidth: 48, // half-width used for hit tests below
  beamPlayerDps: 40, // damage/sec to the player
  beamEnemyDps: 240, // damage/sec to OTHER hostiles caught in it
  beamBodyDps: 1500, // ore/sec carved from asteroids/moons (destroys them)
  // --- rewards for the kill ---
  killXp: 6000,
  killUnits: 2000,
  // --- burning wreckage ---
  fireCountMin: 2, // 2..3 fires on a destroyed segment
  fireCountMax: 3,
  // --- awakening cutscene ---
  explodedPlanetScale: 1.4, // exploded planet is 40% bigger than the original
  cutsceneFreeze: 2.6, // seconds the world is frozen for the dialogue
  emergeTime: 3.2, // seconds for the head to climb out + body to unwrap
  spriteWorldHeight: 90, // default (body) — used by the render scale fallback
} as const;

/**
 * PLAYER_FIGHTER — escort fighters the player buys (Mk IV Fighter Bays) and that
 * fly out to fight during combat. Base stats deliberately MATCH the enemy fighter
 * (same size, speed, fire rate); an assigned pilot's buff multiplies speed, HP,
 * shield and fire rate on top (pilotMul defaults to 1 until a pilot is assigned).
 */
export const PLAYER_FIGHTER = {
  spriteWorldHeight: ENEMY.spriteWorldHeight,
  speed: ENEMY.speed,
  patrolSpeed: ENEMY.patrolSpeed,
  turnRate: ENEMY.turnRate,
  passRange: ENEMY.passRange,
  straightTime: ENEMY.straightTime,
  maxHp: ENEMY.maxHp,
  maxShield: ENEMY.maxShield,
  shieldRegenPerSec: ENEMY.shieldRegenPerSec,
  regenDelay: ENEMY.regenDelay,
  collisionRadius: ENEMY.collisionRadius,
  avoidRange: ENEMY.avoidRange,
  turretRange: ENEMY.turretRange,
  fireCone: ENEMY.fireCone,
  fireInterval: ENEMY.fireInterval,
  boltsPerShot: ENEMY.boltsPerShot,
  barrelSpread: ENEMY.barrelSpread,
  boltSpeed: ENEMY.boltSpeed,
  boltDamage: ENEMY.boltDamage,
  boltLife: ENEMY.boltLife,
  muzzleLen: ENEMY.muzzleLen,
  mounts: ENEMY.mounts,
  seekRange: 1700, // chase any hostile within this of the hull
  rallyRadius: 230, // idle orbit distance around the hull with no target
  leashRange: 950, // never engage/stray beyond this from the mothership (stay close)
  guardRange: 340, // tight leash while recalled to defend the mothership (Formation in combat)
  buyCost: 200, // Units to buy one escort fighter
  maxSlots: 16, // hard cap on fighter bays (Mk VI Swarm-lord = 16; MK V = 12; others usable-capped at 8)
} as const;

/**
 * FIGHTER DRONES — the Mk V Swarm-lord's mini-fighters. Same AI as escort fighters
 * but smaller, faster, and flimsier, and they carry no pilot (the Fighter Drone
 * Controller crew buffs the whole swarm). Unlocked in +N chunks by Swarm-lord
 * drone-bay upgrades, capped at 12. They launch from the wing runways too.
 */
export const FIGHTER_DRONE = {
  maxSlots: 28, // render-pool + tier cap (Mk VI Swarm-lord grows to 28; MK V usable-capped at 12)
  sizeMul: 0.6, // renders 40% smaller than a normal fighter
  speed: PLAYER_FIGHTER.speed * 1.3, // faster
  maxHp: PLAYER_FIGHTER.maxHp * 0.5, // weaker
  maxShield: PLAYER_FIGHTER.maxShield * 0.5,
  boltDamage: PLAYER_FIGHTER.boltDamage * 0.6, // lower damage
} as const;

/**
 * MISSILE SALVO — the Mk V Swarm-lord's homing-missile barrage. Salvo size grows
 * via upgrade nodes (+4 each, capped at 12). Fired on a button; each missile
 * chases a DIFFERENT enemy, then self-destructs after `life` seconds.
 */
export const MISSILE = {
  maxCount: 28, // hard cap (Mk V Pods give +4 ×4 = 16; Mk VI Swarm-lord Pods add +12 → 28)
  cooldown: 20, // seconds between salvos (manual skill on the right rail)
  life: 5, // seconds homing before it detonates itself
  launchSpeed: 180, // px/s out of the tubes (+20%)
  speed: 432, // px/s cruise (accelerates up to this) — +20%
  accel: 672, // px/s² acceleration — +20% so it reaches cruise as quickly
  turnRate: 4.2, // rad/s homing turn
  damage: 58, // per missile (direct + splash) — +30%
  blastRadius: 74, // splash-damage radius on detonation
  hitRadius: 22, // contact-detonation radius
  // Launch pattern: 2 at a time (1 per wing) every batchInterval, each fired straight
  // forward from a point that walks out along the wing (root→tip) as the salvo runs.
  batchInterval: 0.25, // seconds between each 2-missile batch
  launchStraight: 1, // seconds flying straight out before it starts homing
  branchStraightTime: 0.5, // BRANCH missiles fly straight this long before finding a new target
} as const;

/**
 * FIGHTER WING MISSILES — Mk VI Swarm-lord ability (unlock node). Each deployed escort
 * fighter auto-launches a small homing-missile volley at the nearest enemy on its own cooldown.
 */
export const FIGHTER_MISSILE = {
  count: 2, // missiles per fighter per volley
  cooldown: 15, // seconds between a fighter's volleys
  damage: 40, // per missile (direct + splash)
  speed: 380, // px/s cruise
  turnRate: 4.6, // rad/s homing turn
  life: 4, // seconds before self-detonate
  hitRadius: 18, // contact-detonation radius
  blastRadius: 42, // splash radius
  launchStraight: 0.25, // seconds flying straight before homing
} as const;

/**
 * FIGHTER-DRONE LANCE — Mk VI Swarm-lord ability (unlock node). Each drone fires a short
 * combat beam at the nearest enemy on its own cooldown; the beam also vaporises any asteroid/
 * moon it crosses (mining is a side effect — the primary target is enemies).
 */

/**
 * ORBITAL RING STATION — a boss encircling a rogue planet with TWO counter-rotating
 * armored rings (one sprite, Enemy_Station.png, rendered at two scales). Each ring
 * carries turrets, cannons (shielded), big cannons (shielded) and 2 hangar gates that
 * launch fighters + a cruiser. Destroy every weapon on a ring and it stops spinning,
 * darkens and burns; the two rings die independently.
 *
 * Mount offsets below are IMAGE-centre-relative texture px (mapper origin 512,768).
 * The ring's true centre is 27.65px higher, so the game adds `mapCenterDy` to each oy.
 */
export const STATION = {
  mapCenterDy: 27.65, // 768 (image cy) − 740.35 (ring cy): shift mounts onto the ring centre
  outerScale: 1.02, // texture px → world, OUTER ring (outer edge ≈ r510), spins CW
  innerScale: 0.586, // ... INNER ring (outer edge ≈ r293), spins CCW
  outerSpin: 0.12, // rad/s (clockwise)
  innerSpin: 0.18, // rad/s (applied counter-clockwise)

  // --- per-weapon combat (tunable) ---
  turretHp: 140, // ×2 — guns were dying too fast
  turretRange: 620,
  turretFireInterval: 1.3,
  turretDamage: 4, // ÷2 — was hitting the player too hard
  turretWorldSize: 12,

  cannonHp: 300, // ×2
  cannonShield: 80, // cannons carry a shield
  cannonRange: 760,
  cannonFireInterval: 2.2,
  cannonDamage: 8, // ÷2
  cannonWorldSize: 16,

  bigCannonHp: 640, // ×2
  bigCannonShield: 200, // big cannons carry a bigger shield
  bigCannonRange: 1000,
  bigCannonFireInterval: 5.0,
  bigCannonBeamDuration: 1.6,
  bigCannonDps: 17, // ÷2 — continuous beam damage/s
  bigCannonWorldSize: 34,

  // --- firing (shared) ---
  aimTurnRate: 2.6, // rad/s the guns swing toward the player
  fireCone: 0.28, // rad — a gun fires once its barrel is this close to lined up
  boltSpeed: 470, // turret/cannon bolt travel (px/s)
  boltLife: 2.4, // seconds before a bolt fizzles
  beamMaxLength: 1100, // big-cannon beam reach
  beamWidth: 9, // big-cannon beam half-width for hit detection
  playerHitRadius: 26, // bolt contact radius on the player hull

  hangarHp: 220, // the hangar gate itself is destructible
  hangarWorldSize: 24,

  // --- hangar launches (one-shot, like the enemy Carrier's fighters) ---
  hangarFighters: 4,
  hangarCruisers: 1,
  launchStraightTime: 1.0, // fly straight out this long before engaging
  launchInterval: 0.4, // stagger between craft leaving a gate

  // --- spawn ---
  fromWarp: 6, // rogue-planet stations start appearing at jump 6
  spawnChance: 0.3, // per rogue planet
  maxPerSystem: 2,

  // Mount offsets — IMAGE-centre texture px (game adds mapCenterDy to oy → ring centre).
  mounts: {
    turrets: [
      { ox: 254, oy: -396 }, { ox: -254, oy: -396 },
      { ox: 399, oy: -190 }, { ox: -399, oy: -190 },
      { ox: 385, oy: -122 }, { ox: -385, oy: -122 },
      { ox: 443, oy: -129 }, { ox: -443, oy: -129 },
      { ox: 385, oy: 79 }, { ox: -385, oy: 79 },
      { ox: 436, oy: 89 }, { ox: -436, oy: 89 },
      { ox: 346, oy: 164 }, { ox: -346, oy: 164 },
      { ox: 402, oy: 188 }, { ox: -402, oy: 188 },
      { ox: -202, oy: 307 }, { ox: 202, oy: 307 },
      { ox: -136, oy: 346 }, { ox: 136, oy: 346 },
      { ox: -226, oy: 365 }, { ox: 226, oy: 365 },
      { ox: -153, oy: 401 }, { ox: 153, oy: 401 },
    ],
    cannons: [
      { ox: 423, oy: -52 }, { ox: -423, oy: -52 },
      { ox: 423, oy: 24 }, { ox: -423, oy: 24 },
      { ox: -47, oy: -449 }, { ox: 47, oy: -449 },
      { ox: -42, oy: 397 }, { ox: 42, oy: 397 },
    ],
    bigCannons: [
      { ox: 302, oy: 268 }, { ox: -302, oy: 268 },
      { ox: 150, oy: -437 }, { ox: -150, oy: -437 },
    ],
    gates: [
      { ox: 314, oy: -277 }, { ox: -314, oy: -277 },
    ],
  },
} as const;

/**
 * MK VII — the SECRET "Eater of Worlds" hull. The player becomes a worm: a head they
 * steer, body segments trailing behind, ending in a tail. It mines by passing THROUGH
 * asteroids/moons (instantly consumed), fires a high-damage beam straight out of the
 * head, and mounts one turret per body/tail segment. No thrusters.
 */
export const MKVII = {
  // --- refit (unlocked only once the player owns the EOW parts, which are then consumed) ---
  refitCost: 20_000_000,
  refitUnits: 100_000,
  reqItems: { eowHead: 1, eowBody: 10, eowTail: 1, eowEnergyCore: 1 },
  hullHpBonus: 4000, // a tanky end-game hull

  // --- the segment chain ---
  startBodies: 10, // + head + tail = 12 segments to start
  maxBodies: 26,
  spacing: 68, // world units between segment centres (segments overlap for a continuous body)
  trailStep: 6, // head path sampled every 6 units travelled
  headWorldHeight: 150,
  bodyWorldHeight: 108,
  tailWorldHeight: 124,
  collisionRadius: 56, // passthrough-mining reach around each segment

  // --- head maw beam: a big beam that CREEPS out from the mouth (like the big cannons)
  //     for 2s, then SPLITS into a 5-beam fan for 1s, then cools down. ---
  beamChargeTime: 1.5, // manual ability: charge up this long before the beam fires
  beamRange: 1290, // beam reach (+50% forward)
  beamWidth: 34, // big-beam half-width for hit detection (+50% girth again)
  beamDps: 210, // big-beam damage/second to foes caught in it
  beamGrowTime: 1.0, // the big beam creeps to full length over this long (−50% speed)
  beamBigDuration: 2.0, // the single big-beam phase
  beamFanDuration: 1.8, // then it fans into small beams for this long (sustained)
  beamFanCount: 5, // number of small beams in the fan
  beamFanSpread: 0.95, // total fan angle (radians) — the fan's resting spread
  beamFanOpenTime: 1.2, // the fan opens gradually over this long (slow, not snappy)
  beamFanSwipeAmp: 0.45, // EACH split beam wanders by up to this (radians) on its own...
  beamFanSwipeFreqMin: 1.8, // ...at a random rate in this range (rad/s)
  beamFanSwipeFreqMax: 5.5,
  beamFanWidth: 9, // small-beam half-width
  beamFanDps: 120, // per small-beam damage/second (each split beam damages)
  beamFadeTime: 0.45, // the beam fades out over the last part of the fan
  beamCooldown: 10.0, // pause before the next volley

  // --- red homing missiles (manual ability): fire from the mouth sides, straight 0.5s, then home ---
  missileMax: 20, // hard cap on a salvo
  missilePerNode: 5, // each upgrade node adds this many
  missileStraightTime: 0.5, // fly straight out before homing
  missileSpeed: 540,
  missileTurnRate: 3.4, // homing turn rate (rad/s)
  missileDamage: 55,
  missileLife: 4.0,
  missileHitRadius: 34,
  missileCooldown: 6.0, // salvo cooldown

  // --- per-segment turret (one on each body + tail segment) ---
  turretRange: 700,
  turretFireInterval: 0.9,
  turretDamage: 14,
  turretBoltSpeed: 620,
  turretBoltLife: 1.6,
  turretHitRadius: 30, // bolt contact radius on a foe
  turretWorldSize: 24,
} as const;

/** Mk VIII "Time & Space Rift" ability — sweep a laser to carve a rift open in front of the
 * worm, then dive through it and re-emerge (segment by segment) at the spot the player tapped
 * on the minimap. A long-range teleport whose sweep also damages foes + mines rocks. */
export const RIFT = {
  frontDist: 450, // the entry rift opens this far ahead of the head (+50% outward)
  halfLen: 300, // half the rift slit's length — the sweep spans left..right this far
  sweepTime: 1.0, // the laser sweeps left→right over this long
  sweepDps: 1300, // heavy damage/second to any foe the sweep laser crosses
  sweepWidth: 34, // sweep-beam half-width for hit detection (rocks add their radius)
  openDelay: 1.0, // wait this long after the sweep ends, then open the rift
  openTime: 0.55, // closed→open vertical expand (a closed eye opening)
  enterTime: 2.0, // the worm slides INTO the entry rift at half speed (head-first, segments follow)
  emergeTime: 1.5, // the worm slides OUT of the exit rift, bit by bit
  closeTime: 0.5, // open→closed once the worm is fully out
  fadeTime: 0.6, // the rift fades away after closing
  cooldown: 22, // seconds before the ability can be used again
  riftWorldWidth: 620, // rendered slit length (a touch wider than the sweep span)
} as const;

/**
 * WARP JUMP cutscene — the animated jump between solar systems. On confirm, the world FREEZES
 * (planets stop revolving), the planets + sun spaghettify into thin streaks pulled toward a
 * rift that opens ahead of the ship (closed → open, like the Mk VIII Time & Space Rift), the
 * ship slides in, and it emerges from the rift in the brand-new system. The rift slit length
 * scales with the mothership's rendered size (`riftSizeMul × hull render height`).
 */
export const WARP = {
  collapseTime: 1.8, // planets + sun stretch into streaks; the (closed) rift fades in ahead
  openTime: 0.7, // rift closed → open (a closed eye opening)
  enterTime: 1.05, // the ship slides INTO the rift, shrinking as it crosses the threshold
  emergeTime: 1.3, // the ship grows back OUT of the exit rift in the destination system
  closeTime: 0.7, // rift open → closed, then fades
  frontDist: 300, // the entry rift opens this far ahead of the ship's nose
  emergeDist: 260, // how far the ship slides forward out of the exit rift
  riftSizeMul: 1.35, // rift slit length = mothership render height × this (so the ship fits through)
  navScale: 0.25, // the system-view hull scale — the cutscene grows the hull from/back to this
  // MK VII/VIII worm hull: it can't shrink into a point, so instead the HEAD dives through the
  // rift and the body segments follow through it one by one (like the Mk VIII rift ability). These
  // give the long body enough time to thread in/out (the ship-scale timings above are too quick).
  wormEnterTime: 1.9, // head + body thread INTO the entry rift, head-first
  wormEmergeTime: 1.9, // head + body slide OUT of the exit rift, bit by bit
} as const;

/** Mk VIII "Maelstrom Coil" ability — the worm curls into a spinning spiral in place: every
 * segment fires an outward SWEEPING laser (each with its own wander) at half the maw beam's
 * damage, and a shield bubble covers the whole coil, searing anything caught inside. Rooted. */
export const COIL = {
  formTime: 3.0, // the whole body winds into the coil over this long (head-first) — half speed
  activeTime: 6.0, // sweeping-laser + shield phase (seconds) AFTER the coil finishes forming
  cooldown: 20, // seconds before the ability is ready again
  loopGapMul: 0.85, // radial gap between the coil's loops = spacing × this (a tight spiral disc)
  shieldMargin: 60, // shield-bubble radius = the coil's bounding radius + this
  laserRange: 558, // how far each segment's laser reaches outward (+20%)
  laserGrowTime: 1.0, // each laser CREEPS out to full length over this long (20% slower creep)
  laserWidth: 11, // laser half-width for hit detection (rocks add their own radius)
  laserDpsMul: 0.5, // each segment laser deals this × the head maw-beam's dps
  laserSwipeAmp: 0.5, // each laser wanders by up to this (radians)…
  laserSwipeFreqMin: 0.6, // …at a random rate in this range (rad/s) — sweep 20% slower
  laserSwipeFreqMax: 2.0,
  shieldHp: 700, // the bubble soaks this much incoming damage before it pops
  shieldDps: 120, // damage/second to any foe inside the bubble
} as const;

/** World-size progression — the map grows richer with each warp jump. */
export const PROGRESSION = {
  bigMoonsBase: 5,
  bigMoonsMax: 10,
  smallMoonsBase: 3,
  smallMoonsMax: 7,
  planetsBase: 8, // max planets at warp 0 (min stays 3)
  // Deeper space: the planet layer (area + asteroids + enemies) grows by `layerGrowMul` once at
  // jump `layerGrowWarp1`, then again at `layerGrowWarp2`.
  layerGrowWarp1: 5,
  layerGrowWarp2: 10,
  layerGrowMul: 1.2,
} as const;

/** Rogue planets — hostile worlds whose MOONS carry destructible auto-turrets.
 * They begin appearing around jump 7 and their resources are worth much more. */
export const MOON_TURRET = {
  fromWarp: 7, // rogue planets + moon turrets start appearing at this jump
  rogueChance: 0.5, // chance a non-service planet is "rogue" once warp >= fromWarp
  chanceBase: 0.7, // per-moon turret chance at fromWarp
  chancePerWarp: 0.05, // +5% each further jump…
  chanceMaxWarp: 13, // …hitting 100% here
  resourceMul: 3, // rogue-planet body ore ×this (3× richer)
  sizeMul: 0.4, // turret radius = moon radius × this (always centred on the moon)
  hpPerRadius: 11.7, // turret maxHp = round(moon radius × this) — +30%
  dmgPerRadius: 0.35, // bolt damage = round(moon radius × this) (min 3)
  aggroRange: 468, // aggro the player within this range — +30%
  fireRange: 494, // +30% so it engages from the extended range
  fireInterval: 1.1,
  boltSpeed: 380,
  boltLife: 1.6,
  turnRate: 2.6, // aim slew (rad/s) — 360° turning
} as const;

/** Item drop chances when a body is destroyed. */
export const DROPS = {
  moonCoreBig: 0.1, // 10% from a full moon
  moonCoreSmall: 0.05, // 5% from a sub-moon
  darkMatterAsteroid: 0.05, // 5% from any asteroid
} as const;

/** Material → Universal Unit exchange (used at trade-hub planets). */
export const TRADE = {
  materialPerUnit: 100, // 100 minerals = 1 Universal Unit
} as const;

/** Ship modifications (shield generators for now) — built/sold at the trade hub. */
export const MODS = {
  buildBaseCost: 60, // Units to build a mod at tier 1
  costPerTier: 55, // +Units per mod tier
  tierEveryWarps: 3, // the trade-hub mod tier rises every N warp jumps
  sellFraction: 0.4, // mod sell value = shieldBonus × this (in Units)
  // --- generator upgrades (done at the 🔧 mechanical yard, paid in Units) ---
  maxModLevel: 5, // a generator can be upgraded to this level
  upgradeShieldMul: 1.28, // shieldBonus ×= this per upgrade level
  upgradeRegenAdd: 0.12, // regenMul += this per upgrade level
  upgradeCostPerShield: 6, // Units cost ≈ shield gained × this
} as const;

/** Units gained when selling a stacking inventory item (trade-hub Sell ribbon). */
export const ITEM_SELL: Record<string, number> = {
  moonCore: 8,
  energyCore: 55,
  darkMatter: 6,
  warpFuel: 40,
  turret_basic: 28,
};

/** Planet services (trade / mechanical) only open when flying near the hub. */
export const SERVICE = {
  interactMargin: 150, // within planet.radius + this of the central hub
} as const;

/** Build costs (Build pillar of the core loop). */
export const BUILD = {
  /** Cost to manufacture one mining drone. Forces early manual mining. */
  drone: { minerals: 60 },
} as const;

/**
 * Mining laser — directional targeting + wandering impact (VFX overhaul).
 * The hull only mines an asteroid it is FACING: the rock must fall inside a
 * detection cone projected from the ship's nose.
 */
export const MINING_LASER = {
  coneHalfAngleDeg: 42, // half-angle of the detection cone (wide)
  muzzleOffset: 46, // front-node distance from hull centre (world units)
  wanderInterval: 3, // seconds between impact-point refreshes
  impactRadiusFrac: 0.8, // impact stays within this fraction of the rock's radius
  // Beam origins = the two drill "guns" at the front of the hull sprite.
  // Measured from MTS_1's alpha channel (the art has large transparent padding,
  // so the drill tips are much closer to centre than the sprite bounds suggest).
  gunForward: 12, // beams leave the Miner's pointy nose tip (~+11 world units)
  gunSpread: 4, // half-distance between the twin nose drills (perpendicular)
} as const;

/**
 * Mechanical (repair) drones — unlocked by an upgrade after the Mk II refit.
 * They launch like mining drones (out the Mk III side gates), orbit the hull and
 * beam it with GREEN twin beams, repairing HP for minerals. Off during combat.
 */
export const MECH_DRONE = {
  mineralPerHp: 3, // 3 material spent per 1 hull HP restored
  repairPerDroneSec: 0.5, // hull HP restored per second per beaming drone (slow trickle)
  beamsPerDrone: 2, // twin green repair beams
} as const;

/**
 * Hangar — the bay on the mothership that launches/recovers mining drones.
 * `maxConcurrent` scales with hangar level, so upgrading deploys more drones.
 */
export const HANGAR = {
  offsetForward: -6, // bay position on the hull, just behind centre (world units)
  offsetLateral: 0,
  startLevel: 2, // starting number of simultaneous drones
  upgradeCost: { minerals: 120 }, // cost per +1 hangar level
} as const;

/**
 * Subordinate mining drone (hangar-deployed). Rather than orbiting uniformly, a
 * drone darts to a random firing position, locks a random attack point on the
 * rock for `attackHold` seconds, then relocates and re-aims — so the swarm looks
 * chaotic and aggressive, not like a clock.
 */
export const MINING_DRONE = {
  deployRange: 210, // radius of the circle around the ship drones will work in
  speed: 150, // px/s travel between firing positions
  cargoCapacity: 40, // minerals hauled per round-trip
  mineRatePerSec: 12, // extraction rate while attacking (slowed to pace progression)
  spawnInterval: 0.5, // seconds between staggered launches
  arriveRadius: 6, // arrival tolerance (firing spot / hangar)
  worldSize: 11, // sprite display size (drones are MTS_1 hulls; scaled down with the fleet)
  beamRadius: 0.4, // thin 3D beam (thinner than the hull's primary laser)
  attackHold: 3, // seconds locked on one attack point before relocating
  spotDistMin: 34, // firing-position distance from the asteroid centre
  spotDistMax: 68,
  attackPointFrac: 0.75, // attack point within this fraction of the rock's radius
  launchTimeMin: 1, // fixed 1s runway takeoff straight out the hull's front…
  launchTimeMax: 1, // …before curving toward the target
  turnRate: 4.5, // rad/s steering cap → tighter turns so drones stop circling rocks
} as const;
